import Api from './api';

/**
 * Content Moderation admin API.
 *
 * Thin wrappers over the shared `Api` client for the 6 admin moderation
 * endpoints. All are admin-only (the bearer token is attached automatically by
 * `Api`). They use `{ silent: true }` so callers can handle errors inline
 * (notably a 403 -> access-denied state) instead of the global error toast.
 *
 * Responses are the raw envelope: `{ data, pagination?, msg? }` on success, or
 * `{ err, status }` when `{ silent: true }` and the request failed.
 *
 * Note: `REACT_APP_API_URL` already ends in `/api`, so paths start at `/admin`.
 */

const buildQuery = (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '' && value !== 'all') {
      params.append(key, String(value));
    }
  });
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

/** GET /api/admin/moderation/getPolicy -> { data: ModerationPolicy } */
export const getModerationPolicy = () =>
  Api.get('/admin/moderation/getPolicy', null, { silent: true });

/**
 * POST /api/admin/moderation/updatePolicy -> { data: ModerationPolicy, msg }
 * Send only the fields that changed (partial update is supported); each
 * category you include must be a full { enabled, warnAt, flagAt, blockAt }.
 */
export const updateModerationPolicy = (updates) =>
  Api.post('/admin/moderation/updatePolicy', updates, null, { silent: true });

/** GET /api/admin/moderation/logs -> { data: { logs }, pagination } */
export const getModerationLogs = (filters = {}) =>
  Api.get(`/admin/moderation/logs${buildQuery(filters)}`, null, { silent: true });

/**
 * GET /api/admin/moderation/content/:logId -> { data: { log, chain } }
 * chain is root-first: e.g. [Post, Comment, Reply]. Empty for blocked text
 * that was never saved (render the log's textSnippet instead).
 */
export const getModerationContent = (logId) =>
  Api.get(`/admin/moderation/content/${logId}`, null, { silent: true });

/** GET /api/admin/moderation/flagged -> { data: { items }, pagination } */
export const getFlaggedContent = (filters = {}) =>
  Api.get(`/admin/moderation/flagged${buildQuery(filters)}`, null, { silent: true });

/** POST /api/admin/moderation/approve/:logId -> { data: { statusApplied }, msg } */
export const approveModerationLog = (logId) =>
  Api.post(`/admin/moderation/approve/${logId}`, {}, null, { silent: true });

/** POST /api/admin/moderation/reject/:logId -> { data: { statusApplied }, msg } */
export const rejectModerationLog = (logId) =>
  Api.post(`/admin/moderation/reject/${logId}`, {}, null, { silent: true });
